import { AlertCircle, XCircle } from "lucide-react";
import { cn } from "@/utils/utils";

export interface TErrorMessageProps {
    title?: string;
    message: string;
    variant?: "inline" | "block";
    className?: string;
}

export function ErrorMessage({
    title,
    message,
    variant = "inline",
    className,
}: TErrorMessageProps) {
    if (variant === "inline") {
        return (
            <div
                className={cn("flex items-center gap-2 text-sm text-destructive", className)}
                role="alert"
                data-testid="error-message"
            >
                <AlertCircle className="h-4 w-4" />
                <span>{message}</span>
            </div>
        );
    }

    return (
        <div
            className={cn("rounded-lg bg-destructive/10 p-4", className)}
            role="alert"
            data-testid="error-message"
        >
            <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="flex-1">
                    {title && <h4 className="font-medium text-destructive">{title}</h4>}
                    <p className="text-sm text-destructive/90">{message}</p>
                </div>
            </div>
        </div>
    );
}

/**
 * Full-page error display
 */
export function FullPageError({
    title = "Something went wrong",
    message,
    onRetry,
}: {
    title?: string;
    message: string;
    onRetry?: () => void;
}) {
    return (
        <div className="flex h-screen w-screen items-center justify-center bg-background p-4">
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
                <XCircle className="h-16 w-16 text-destructive" />
                <h1 className="text-2xl font-semibold">{title}</h1>
                <p className="text-muted-foreground">{message}</p>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        Try Again
                    </button>
                )}
            </div>
        </div>
    );
}
